import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  MessageCircle, X, Send, Sparkles, Trash2, 
  ChevronDown, Loader2, Bot, User, Lightbulb,
  ClipboardList, Users, Megaphone, HelpCircle, GripVertical,
  Minimize2, Maximize2, ThumbsUp, ThumbsDown, Brain, ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useLocation, useNavigate } from 'react-router-dom';

import { getApiUrl, getBaseUrl } from '../utils/api';
import { useContextualAgent, fetchAllAgents } from '../hooks/useContextualAgent';
import { ChatMessageContent } from './ChatMessageContent';
import { ProposalCard } from './chat/ProposalCard';
import { useIsMobile } from '../hooks/useMobileLayout';
import { getScreenState } from '../utils/screenState';
import { VoiceAskButton, SpeakButton } from './chat/VoiceControls';
import { MapPin, History } from 'lucide-react';
import { cleExpert, visageUrl } from './ig/VisageExpert';

const API = getApiUrl();
const BASE = getBaseUrl();

// Resolve avatar URL (backend returns "/api/static/..."). Prepend base only when
// we're on the preview env where /api maps to a separate origin.
const avatarUrl = (path) => {
  if (!path) return null;
  const k = cleExpert((String(path).match(/agents\/([a-z]+)\.(png|webp)/) || [])[1]);
  if (k) return visageUrl(k, 128);  // LOT 72 · le vrai visage de l'agent, jamais la lettre
  if (/^https?:\/\//.test(path) || path.startsWith('/brand/')) return path;
  return `${BASE}${path}`;
};

// Category icons and colors
const categoryConfig = {
  operations: { icon: ClipboardList, color: 'bg-blue-500', label: 'Opérations' },
  recrutement: { icon: Users, color: 'bg-[var(--brand)]', label: 'Recrutement' },
  marketing: { icon: Megaphone, color: 'bg-pink-500', label: 'Marketing' },
  support: { icon: HelpCircle, color: 'bg-orange-500', label: 'Support' }
};

// Draggable position storage key
const POSITION_KEY = 'ai_chat_bubble_position';
const HEADER_COLORS = { sabril: '#304640', sophie: '#9d3155', leo: '#356c41', manu: '#24538a', theo: '#735217', clara: '#773459' };

export const AIChatBubble = () => {
  const mobile = useIsMobile();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const screenPath = pathname + search;
  const [screenLabel, setScreenLabel] = useState('');
  const [prevSessions, setPrevSessions] = useState([]);
  const [viewSession, setViewSession] = useState(null); // conversation précédente ouverte en lecture seule
  const [helpSteps, setHelpSteps] = useState(null);
  const [helpMode, setHelpMode] = useState(false);
  const questionnaireRoute = pathname.endsWith('/haccp/passeport') || pathname.endsWith('/mon-abonnement');
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [scenarios, setScenarios] = useState({});
  const [showScenarios, setShowScenarios] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAgentPicker, setShowAgentPicker] = useState(false);
  const [allAgents, setAllAgents] = useState([]);
  const [agentOverride, setAgentOverride] = useState(null); // user-picked agent key
  // V6.0.21 — Per-agent memory: thumbs-up + side panel listing patron's validated insights
  const [memorySaved, setMemorySaved] = useState({}); // {idx: true} — already 👍'd
  const [showMemoryPanel, setShowMemoryPanel] = useState(false);
  const [agentMemories, setAgentMemories] = useState([]);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const conversationVersion = useRef(0);
  const pendingAsk = useRef(null);

  // V5.99.4 — pick the right Pixar AI persona based on the current route (or user override)
  const contextualAgent = useContextualAgent(agentOverride);
  const agent = contextualAgent;
  useEffect(() => { setAgentOverride(null); setSuggestions([]); setScenarios({}); setShowScenarios(false); }, [pathname]);
  
  // Draggable position for closed state
  const [bubblePosition, setBubblePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // Ouverture externe sur un expert précis (bandeau stories de l'accueil)
  useEffect(() => {
    const h = (e) => { const k = e.detail?.key; if (k) setAgentOverride(k); if (e.detail?.message) { if (e.detail.autoSend) pendingAsk.current = e.detail.message; else setInputValue(e.detail.message); } setHelpMode(!!e.detail?.help); setIsOpen(true); };
    window.addEventListener('sabril:open-agent', h);
    // Lien d'écran dans une réponse (« Finance › Résultat ») : on y va et la bulle se ferme.
    const go = (e) => { const p = e.detail?.path; if (!p) return; const slug = window.location.pathname.split('/')[1]; navigate(`/${slug}${p}`); setIsOpen(false); };
    window.addEventListener('sabril:navigate', go);
    return () => { window.removeEventListener('sabril:open-agent', h); window.removeEventListener('sabril:navigate', go); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved position on mount
  useEffect(() => {
    const saved = localStorage.getItem(POSITION_KEY);
    if (saved) {
      try {
        setBubblePosition(JSON.parse(saved));
      } catch {
        setBubblePosition({ x: 0, y: 0 });
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    conversationVersion.current += 1;
    setIsLoading(false);
    if (isOpen) {
      setMessages([]);
      setSuggestions([]);
      setScenarios({});
      setViewSession(null);
      setHistoryLoading(true);
      loadHistory(controller.signal);
      loadSuggestions(controller.signal);
      loadScenarios(controller.signal);
      loadPrevSessions(controller.signal);
      loadHelpSteps(controller.signal);
      fetchAllAgents().then(setAllAgents);
      // Focus input when opened
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    // V6.0.21 — Reset per-message thumbs-up state on agent change
    setMemorySaved({});
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, agent?.key, screenPath]);

  // V6.0.21 — Auto-load memory panel content whenever it's opened or the agent changes
  useEffect(() => {
    if (isOpen && showMemoryPanel) loadAgentMemories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showMemoryPanel, agent?.key]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // « Demander de l'aide » : envoie automatiquement la question contextuelle dès que l'historique est chargé
  useEffect(() => {
    if (isOpen && !historyLoading && pendingAsk.current) { const q = pendingAsk.current; pendingAsk.current = null; sendMessage(q); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, historyLoading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadHistory = async (signal, sessionId = null) => {
    try {
      // Une conversation par écran : l'historique est celui de cet expert sur CET écran (module + onglet).
      const res = await axios.get(`${API}/ai/chat/history`, {
        params: { agent_key: agent?.key || 'sabril', path: screenPath, ...(sessionId ? { session_id: sessionId } : {}) },
        withCredentials: true,
        signal,
      });
      if (!signal?.aborted) setMessages(res.data || []);
    } catch (error) {
      if (!signal?.aborted) console.error('Error loading history:', error);
    } finally {
      if (!signal?.aborted) setHistoryLoading(false);
    }
  };

  const loadSuggestions = async (signal) => {
    try {
      const res = await axios.get(`${API}/ai/suggestions`, { params: { agent_key: agent.key, path: screenPath }, withCredentials: true, signal });
      if (!signal?.aborted) { setSuggestions(res.data.suggestions || []); setScreenLabel(res.data.screen_label || ''); }
    } catch (error) {
      if (!axios.isCancel(error)) console.error('Error loading suggestions:', error);
    }
  };

  const loadPrevSessions = async (signal) => {
    try {
      const res = await axios.get(`${API}/ai/chat/sessions`, { params: { agent_key: agent.key, path: screenPath }, withCredentials: true, signal });
      if (!signal?.aborted) setPrevSessions(res.data.sessions || []);
    } catch (error) { if (!axios.isCancel(error)) setPrevSessions([]); }
  };

  const loadHelpSteps = async (signal) => {
    try {
      const res = await axios.get(`${API}/help/page`, { params: { path: screenPath }, withCredentials: true, signal });
      if (!signal?.aborted) setHelpSteps(res.data?.steps?.length ? res.data : null);
    } catch (error) { if (!axios.isCancel(error)) setHelpSteps(null); }
  };

  const openPrevSession = async (sess) => {
    setViewSession(sess); setHistoryLoading(true); setMessages([]);
    await loadHistory(undefined, sess.session_id);
  };

  const backToScreen = async () => {
    setViewSession(null); setHistoryLoading(true); setMessages([]);
    await loadHistory(undefined);
  };

  const loadScenarios = async (signal) => {
    try {
      const res = await axios.get(`${API}/ai/scenarios`, { params: { agent_key: agent.key, path: pathname }, withCredentials: true, signal });
      if (!signal?.aborted) setScenarios(res.data || {});
    } catch (error) {
      if (!axios.isCancel(error)) console.error('Error loading scenarios:', error);
    }
  };

  const sendMessage = async (text = inputValue) => {
    if (!text.trim() || isLoading || historyLoading) return;
    const version = conversationVersion.current;

    const userMessage = {
      role: 'user',
      content: text,
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setShowScenarios(false);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: text,
        agent_key: agent?.key,
        path: window.location.pathname + window.location.search,
        screen: getScreenState(),
        ...(helpMode ? { context_mode: 'page_help' } : {}),
      }, { withCredentials: true, timeout: 130000 });

      const aiMessage = {
        role: 'assistant',
        content: res.data.response,
        handoff_to: res.data.handoff_to || null,
        message_id: res.data.message_id,
        proposals: res.data.proposals || [],
        question: text,
        created_at: new Date().toISOString()
      };
      if (conversationVersion.current === version) {
        if (res.data.handoff_from && res.data.agent?.key === 'sabril') {
          toast.info(`${res.data.handoff_from.name} a transmis votre question à Sabril.`);
          setAgentOverride('sabril');
        } else setMessages(prev => [...prev, aiMessage]);
      }
    } catch (error) {
      if (conversationVersion.current !== version) return;
      console.error('Error sending message:', error);
      toast.error('Erreur de connexion');
      
      const errorMessage = {
        role: 'assistant',
        content: 'Désolé, je rencontre un problème. Réessayez dans quelques instants.',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      if (conversationVersion.current === version) setIsLoading(false);
    }
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/ai/chat/history`, {
        params: { agent_key: agent?.key || 'sabril', path: screenPath },  // conversation de cet écran uniquement
        withCredentials: true,
      });
      setMessages([]);
      setMemorySaved({});
      toast.success('Conversation effacée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const sendFeedback = async (idx, msg, rating) => {
    if (!msg.message_id) return;
    try {
      await axios.post(`${API}/ai/chat/feedback`, { message_id: msg.message_id, rating }, { withCredentials: true });
      setMessages((rows) => rows.map((m, i) => (i === idx ? { ...m, feedback: rating } : m)));
      toast.success(rating === 'up' ? 'Merci, noté !' : 'Merci, on va faire mieux.');
    } catch (err) {
      toast.error('Retour non enregistré');
    }
  };

  // V6.0.21 — Save a single assistant message as a memory for the current agent (👍)
  const saveMessageToMemory = async (idx, content) => {
    if (!agent?.key || !content?.trim()) return;
    try {
      await axios.post(
        `${API}/ai/agents/${agent.key}/memory`,
        { content, kind: 'manual', score: 1.0 },
        { withCredentials: true }
      );
      setMemorySaved((s) => ({ ...s, [idx]: true }));
      toast.success(`Ajouté à la mémoire de ${agent.name}`);
      // Refresh panel count if open
      if (showMemoryPanel) loadAgentMemories();
    } catch (err) {
      toast.error("Impossible d'ajouter à la mémoire");
    }
  };

  // V6.0.21 — Load memories for the side panel
  const loadAgentMemories = async () => {
    if (!agent?.key) return;
    setMemoryLoading(true);
    try {
      const r = await axios.get(`${API}/ai/agents/${agent.key}/memory`, { withCredentials: true });
      setAgentMemories(r.data?.memories || []);
    } catch (err) {
      console.error(err);
    } finally {
      setMemoryLoading(false);
    }
  };

  // V6.0.21 — Forget one memory
  const forgetMemory = async (memory_id) => {
    if (!agent?.key) return;
    try {
      await axios.delete(`${API}/ai/agents/${agent.key}/memory/${memory_id}`, { withCredentials: true });
      setAgentMemories((rows) => rows.filter((m) => m.memory_id !== memory_id));
      toast.success('Mémoire oubliée');
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const selectScenario = async (category, scenarioId) => {
    try {
      const res = await axios.get(`${API}/ai/scenarios/${category}/${scenarioId}`, { params: { agent_key: agent.key, path: pathname }, withCredentials: true });
      const scenario = res.data;
      const prompt = `Je veux de l'aide pour: ${scenario.title}`;
      sendMessage(prompt);
      setShowScenarios(false);
      setSelectedCategory(null);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Handle bubble drag (V5.99.6 — fluid drag, click only opens when not dragged)
  const handleDragEnd = (event, info) => {
    const distance = Math.hypot(info.offset.x, info.offset.y);
    // Only persist as a "real drag" if the user actually moved
    if (distance > 6) {
      const newPos = {
        x: bubblePosition.x + info.offset.x,
        y: bubblePosition.y + info.offset.y,
      };
      // Keep ~48px on every side of the screen so the bubble stays reachable
      const padding = 48;
      newPos.x = Math.max(-window.innerWidth + padding * 2, Math.min(newPos.x, padding));
      newPos.y = Math.max(-window.innerHeight + padding * 2, Math.min(newPos.y, padding));
      setBubblePosition(newPos);
      localStorage.setItem(POSITION_KEY, JSON.stringify(newPos));
      // Block the upcoming synthetic click triggered by Framer
      setTimeout(() => setIsDragging(false), 60);
    } else {
      setIsDragging(false);
    }
  };

  return (
    <>
      {/* Floating Draggable Button (when chat is closed) */}
      <AnimatePresence>
        {!isOpen && !questionnaireRoute && (
          <motion.div
            drag={!mobile}
            dragMomentum={false}
            dragElastic={0.08}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              x: mobile ? 0 : bubblePosition.x,
              y: mobile ? 0 : bubblePosition.y
            }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed z-[60] cursor-grab active:cursor-grabbing touch-none"
            data-testid="ai-floating-anchor"
            style={{ 
              bottom: 160, 
              right: 20,
            }}
            whileDrag={{ scale: 1.08 }}
          >
            <button
              onClick={() => {
                if (!isDragging) setIsOpen(true);
              }}
              className={`relative w-14 h-14 rounded-full shadow-lg bg-gradient-to-br ${agent.color} p-[2px] group`}
              data-testid="ai-chat-button"
              aria-label={`Demandez à ${agent.name}`}
              title={`Demandez à ${agent.name}`}
            >
              <div className="w-full h-full rounded-[14px] overflow-hidden bg-[var(--carte)]">
                {agent.avatar ? (
                  <img
                    src={avatarUrl(agent.avatar)}
                    alt={agent.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    draggable="false"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-green-700">
                    <Bot size={22} className="text-[var(--sur-couleur)]" />
                  </div>
                )}
              </div>
              {/* Tiny status dot (online) */}
              <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-400 rounded-full ring-2 ring-white" />
            </button>
            {/* V6.0.83 — Always-visible label (Apple HIG: never make affordance ambiguous) */}
            <span className="pointer-events-none absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs font-extrabold text-[var(--texte)] bg-white/95 px-1.5 py-0.5 rounded-[14px] shadow-sm whitespace-nowrap">
              {agent.name || 'Sabril'}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fullscreen Chat Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[var(--carte)] flex flex-col"
            data-testid="ai-chat-modal"
          >
            {/* Header */}
            <div className="p-4 text-[var(--sur-couleur)] flex-shrink-0 safe-area-top relative" style={{ backgroundColor: HEADER_COLORS[agent.key] || HEADER_COLORS.sabril }} data-testid="chat-agent-header">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowAgentPicker((v) => !v)}
                  className="flex items-center gap-3 group/agent text-left flex-1 min-w-0"
                  data-testid="agent-switcher-btn"
                  aria-label="Changer d'agent"
                >
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-white/20 ring-2 ring-white/40 shrink-0">
                    {agent.avatar ? (
                      <img src={avatarUrl(agent.avatar)} alt={agent.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Bot size={22} /></div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold flex items-center gap-1.5">
                      {agent.name}
                      <ChevronDown size={14} className="opacity-70 group-hover/agent:opacity-100 transition" />
                    </h3>
                    <p data-testid="chat-agent-role" className="text-xs text-white/85 whitespace-normal break-words">{agent.name} · {agent.role}</p>
                    {(viewSession?.label || screenLabel) && <p data-testid="chat-screen-context" className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-[var(--sur-couleur)] bg-white/15 rounded-full px-2 py-0.5 max-w-full truncate"><MapPin size={11} />{viewSession ? `${viewSession.label} · lecture seule` : screenLabel}</p>}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setShowMemoryPanel((s) => !s)}
                    className={`p-2 hover:bg-white/15 rounded-[14px] transition-colors ${showMemoryPanel ? 'bg-white/20' : ''}`}
                    title={`Mémoire de ${agent?.name || ''}`}
                    data-testid="memory-panel-toggle"
                  >
                    <Brain size={18} />
                  </button>
                  <button
                    onClick={clearHistory}
                    className="p-2 hover:bg-white/15 rounded-[14px] transition-colors"
                    title="Effacer l'historique"
                    data-testid="clear-history-btn"
                  >
                    <Trash2 size={18} />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 hover:bg-white/15 rounded-[14px] transition-colors"
                    data-testid="close-chat-btn"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Agent picker dropdown */}
              <AnimatePresence>
                {showAgentPicker && allAgents.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute left-3 right-3 top-full mt-2 bg-[var(--carte)] text-[var(--texte)] rounded-2xl shadow-2xl border border-[var(--bord)] z-30 overflow-hidden"
                    data-testid="agent-picker-panel"
                  >
                    <div className="p-2 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {allAgents.map((a) => {
                        const active = a.key === agent.key;
                        return (
                          <button
                            key={a.key}
                            onClick={() => { setAgentOverride(a.key); setShowAgentPicker(false); toast.success(`Vous parlez maintenant à ${a.name}`); }}
                            className={`flex items-center gap-2 p-2 rounded-xl text-left transition ${active ? 'bg-[var(--remplissage)] ring-2 ring-emerald-500' : 'hover:bg-[var(--remplissage)]'}`}
                            data-testid={`agent-pick-${a.key}`}
                          >
                            <div className={`w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br ${a.color} p-[2px] shrink-0`}>
                              <div className="w-full h-full rounded-[14px] overflow-hidden bg-[var(--carte)]">
                                <img src={avatarUrl(a.avatar)} alt={a.name} className="w-full h-full object-cover" />
                              </div>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-extrabold leading-tight truncate">{a.name}</p>
                              <p className="text-xs text-[var(--texte-2)] leading-tight truncate">{a.role}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {agentOverride && (
                      <button
                        onClick={() => { setAgentOverride(null); setShowAgentPicker(false); toast.info('Mode automatique réactivé'); }}
                        className="w-full text-center py-2 text-xs font-semibold text-[var(--texte-2)] hover:bg-[var(--remplissage)] border-t border-[var(--bord)]"
                        data-testid="agent-pick-auto"
                      >
                        ⤺ Laisse l'app choisir automatiquement
                      </button>
                    )}
                </motion.div>
                )}
              </AnimatePresence>

              {/* V6.0.21 — Memory side-panel (in-place overlay over the message area) */}
              <AnimatePresence>
                {showMemoryPanel && (
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    className="absolute top-[88px] right-0 bottom-[76px] w-[300px] bg-[var(--carte)] border-l border-[var(--bord)] shadow-xl z-30 flex flex-col"
                    data-testid="memory-panel"
                  >
                    <div className="px-4 py-3 border-b border-[var(--bord)] flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <Brain size={16} className="text-[var(--brand)] shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs uppercase font-semibold text-[var(--texte-2)] tracking-wide">Mémoire de</p>
                          <p className="text-sm font-bold text-[var(--texte)] truncate">{agent?.name}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setShowMemoryPanel(false)}
                        className="p-1 hover:bg-[var(--remplissage)] rounded"
                        data-testid="memory-panel-close"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2">
                      {memoryLoading ? (
                        <div className="flex items-center justify-center text-[var(--texte-2)] py-6">
                          <Loader2 size={14} className="animate-spin mr-1" /> Chargement…
                        </div>
                      ) : agentMemories.length === 0 ? (
                        <div className="text-center py-8 text-xs text-[var(--texte-2)]">
                          <Brain size={28} className="mx-auto mb-2 text-neutral-300" />
                          <p className="font-medium">Pas encore d'insight.</p>
                        </div>
                      ) : (
                        agentMemories.map((m) => (
                          <div
                            key={m.memory_id}
                            className="bg-[var(--remplissage)] border border-[var(--bord)] rounded-lg p-2.5 group/mem"
                            data-testid={`memory-item-${m.memory_id}`}
                          >
                            <div className="flex items-start gap-2">
                              <span className={`shrink-0 text-xs uppercase font-bold px-1.5 py-0.5 rounded ${
                                m.kind === 'manual' ? 'bg-[var(--p-9be38c)] text-[var(--encre)]' : 'bg-[var(--brand-soft)] text-[var(--brand)]'
                              }`}>
                                {m.kind === 'manual' ? '👍' : 'auto'}
                              </span>
                              <p className="text-xs text-[var(--texte)] flex-1 break-words">{m.content}</p>
                            </div>
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-xs text-[var(--texte-2)]">
                                {m.hits > 0 ? `${m.hits} rappels` : 'jamais réutilisé'}
                              </span>
                              <button
                                onClick={() => forgetMemory(m.memory_id)}
                                className="text-xs text-[var(--texte-2)] hover:text-[var(--p-c43c12)] opacity-0 group-hover/mem:opacity-100 transition"
                                data-testid={`memory-forget-${m.memory_id}`}
                              >
                                Oublier
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Messages Area - Scrollable */}
            <div className={`flex-1 overflow-y-auto p-4 space-y-4 bg-[var(--remplissage)] transition-[padding] ${showMemoryPanel ? 'pr-[316px]' : ''}`}>
              {viewSession && <button type="button" onClick={backToScreen} className="w-full text-left text-xs font-semibold text-[var(--brand)] bg-[var(--carte)] border border-[var(--bord)] rounded-xl px-3 py-2" data-testid="chat-back-to-screen">← Revenir à la conversation de cet écran</button>}
              {!viewSession && prevSessions.length > 0 && (
                <details className="bg-[var(--carte)] border border-[var(--bord)] rounded-xl px-3 py-2" data-testid="chat-prev-sessions">
                  <summary className="cursor-pointer text-xs font-semibold text-[var(--texte-2)] inline-flex items-center gap-1.5"><History size={13} /> Conversations précédentes ({prevSessions.length})</summary>
                  <ul className="mt-2 space-y-1">
                    {prevSessions.map((sess) => <li key={sess.session_id}><button type="button" onClick={() => openPrevSession(sess)} className="w-full text-left text-xs rounded-lg px-2 py-1.5 hover:bg-[var(--remplissage)]" data-testid={`chat-prev-session-${sess.session_id}`}><span className="font-semibold text-[var(--texte)]">{sess.label}</span>{sess.first_question && <span className="block text-[var(--texte-2)] truncate">{sess.first_question}</span>}</button></li>)}
                  </ul>
                </details>
              )}
              {messages.length === 0 && !historyLoading && !viewSession && helpSteps && (
                <div className="bg-[var(--carte)] border border-[var(--bord)] rounded-xl p-3 text-left" data-testid="chat-help-steps">
                  <p className="text-xs font-bold text-[var(--texte)] mb-1.5">{helpSteps.title} · comment ça marche</p>
                  <ol className="space-y-1 text-xs text-[var(--texte-2)] list-decimal pl-4">{helpSteps.steps.map((st, i) => <li key={i}>{st}</li>)}</ol>
                </div>
              )}
              {messages.length === 0 && !historyLoading && (
                <div className="text-center py-8">
                  <div className={`w-20 h-20 rounded-full overflow-hidden mx-auto mb-4 bg-gradient-to-br ${agent.color} p-1 shadow-lg`}>
                    <div className="w-full h-full rounded-[14px] overflow-hidden bg-[var(--carte)]">
                      {agent.avatar ? (
                        <img src={avatarUrl(agent.avatar)} alt={agent.name} className="w-full h-full object-cover" />
                      ) : (
                        <Sparkles size={32} className="text-[var(--p-3d9a55)] m-auto" />
                      )}
                    </div>
                  </div>
                  <h4 data-testid="chat-agent-greeting" className="font-bold text-lg text-[var(--texte)]">Salut, moi c'est {agent.name} 👋</h4>
                  <p className="text-sm text-[var(--texte-2)] mt-1 mb-4">
                    {screenLabel ? <>Je réponds sur <strong>{screenLabel}</strong>, avec vos chiffres.</> : <>{agent.tagline || agent.role} — pose-moi vos questions.</>}
                  </p>
                  
                  {/* Quick suggestions */}
                  <div className="space-y-2 max-w-sm mx-auto">
                    {suggestions.slice(0, 4).map((suggestion, idx) => (
                      <button
                        key={idx}
                        data-testid={`chat-starter-${agent.key}-${idx}`}
                        onClick={() => sendMessage(suggestion)}
                        className="w-full text-left p-3 bg-[var(--carte)] border border-[var(--bord)] rounded-xl text-sm text-[var(--texte)] hover:border-[var(--brand)] hover:bg-[var(--brand)]/5 transition-colors"
                      >
                        <Lightbulb size={14} className="inline mr-2 text-[var(--texte)]" />
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`ai-chat-${msg.role}-message-${idx}`}
                >
                  <div className={`min-w-0 max-w-[85%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                    <div className={`flex items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-9 h-9 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0 ${
                        msg.role === 'user' ? 'bg-[var(--brand)]' : `bg-gradient-to-br ${agent.color} p-[2px]`
                      }`}>
                        {msg.role === 'user' ? (
                          <User size={16} className="text-[var(--sur-couleur)]" />
                        ) : agent.avatar ? (
                          <div className="w-full h-full rounded-[14px] overflow-hidden bg-[var(--carte)]">
                            <img src={avatarUrl(agent.avatar)} alt={agent.name} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <Bot size={16} className="text-[var(--sur-couleur)]" />
                        )}
                      </div>
                      <div className={`min-w-0 rounded-2xl px-4 py-3 ${
                        msg.role === 'user' 
                          ? 'bg-[var(--brand)] text-[var(--sur-couleur)] rounded-br-md' 
                          : 'bg-[var(--carte)] border border-[var(--bord)] rounded-bl-md text-[var(--texte)]'
                      }`}>
                        {msg.role === 'assistant' && msg.handoff_from && <p className="text-xs font-semibold mb-2 text-[var(--texte-2)]" data-testid={`chat-transfer-notice-${idx}`}>Transmis par {allAgents.find(a => a.key === msg.handoff_from)?.name || msg.handoff_from} · Sabril prend le relais</p>}
                        {msg.role === 'assistant'
                          ? <><ChatMessageContent content={msg.content} testid={`ai-chat-message-content-${idx}`} />{msg.content && !msg.handoff_to_key && <div className="mt-1.5 -mb-1"><SpeakButton text={msg.content} agentKey={agent?.key} testId={`chat-speak-${idx}`} /></div>}</>
                          : <div className="text-sm whitespace-pre-wrap [overflow-wrap:anywhere]" data-testid={`ai-chat-message-content-${idx}`}>{msg.content}</div>}
                        {msg.handoff_to_key === 'sabril' && <button type="button" data-testid={`chat-open-supervisor-${idx}`} className="mt-3 flex items-center gap-2 text-xs font-semibold text-[var(--texte)] bg-[var(--remplissage)] hover:bg-[var(--remplissage)] px-3 py-2 rounded-lg" onClick={() => setAgentOverride('sabril')}>Lire la réponse de Sabril <ArrowRight size={14} /></button>}
                        {msg.role === 'assistant' && msg.handoff_to && (
                          <button
                            type="button"
                            onClick={() => { setAgentOverride(msg.handoff_to.key); setInputValue(msg.question || ''); toast.success(`${msg.handoff_to.name} prend le relais`); }}
                            className="mt-2 w-full flex items-center gap-2.5 rounded-[14px] bg-[var(--brand-soft)] hover:bg-[var(--brand-soft-2)] px-3 py-2.5 text-left transition-colors min-h-[48px]"
                            data-testid={`chat-handoff-${idx}`}
                          >
                            <img src={avatarUrl(msg.handoff_to.avatar)} alt="" className="w-8 h-8 rounded-full object-cover bg-[var(--carte)] shrink-0" />
                            <span className="flex-1 min-w-0"><span className="block text-[14px] font-semibold text-[var(--texte)]">Basculer vers {msg.handoff_to.name}</span><span className="block text-[12px] text-[var(--texte-2)] truncate">{msg.handoff_to.role} · votre question est reprise</span></span>
                            <ArrowRight size={16} className="text-[var(--brand-pressed)] shrink-0" />
                          </button>
                        )}
                        {msg.role === 'assistant' && (msg.proposals || []).map((p) => (
                          <ProposalCard key={p.proposal_id} proposal={p} />
                        ))}
                        {/* V6.0.21 — Thumbs-up: save this answer to the agent's memory */}
                        {msg.role === 'assistant' && msg.content && !msg.handoff_to_key && (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => saveMessageToMemory(idx, msg.content)}
                              disabled={memorySaved[idx]}
                              className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-[14px] transition ${
                                memorySaved[idx]
                                  ? 'bg-[var(--p-9be38c)] text-[var(--encre)]'
                                  : 'bg-[var(--remplissage)] text-[var(--texte-2)] hover:bg-[var(--p-9be38c)] hover:text-[var(--encre)]'
                              }`}
                              title={memorySaved[idx] ? 'Mémorisé' : `Ajouter à la mémoire de ${agent?.name || ''}`}
                              data-testid={`chat-msg-thumbs-up-${idx}`}
                            >
                              <ThumbsUp size={11} />
                              {memorySaved[idx] ? 'Mémorisé' : 'À retenir'}
                            </button>
                            {msg.message_id && (
                              <span className="ml-auto inline-flex items-center gap-1" data-testid={`chat-msg-feedback-${idx}`}>
                                <button type="button" onClick={() => sendFeedback(idx, msg, 'up')} title="Réponse utile" className={`rounded-[14px] p-1 transition ${msg.feedback === 'up' ? 'bg-[var(--p-9be38c)] text-[var(--encre)]' : 'text-[var(--texte-2)] hover:text-[var(--encre)]'}`} data-testid={`chat-msg-feedback-up-${idx}`}><ThumbsUp size={11} /></button>
                                <button type="button" onClick={() => sendFeedback(idx, msg, 'down')} title="Pas utile" className={`rounded-[14px] p-1 transition ${msg.feedback === 'down' ? 'bg-rose-100 text-[var(--p-c43c12)]' : 'text-[var(--texte-2)] hover:text-[var(--p-c43c12)]'}`} data-testid={`chat-msg-feedback-down-${idx}`}><ThumbsDown size={11} /></button>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}

              {isLoading && (
                <div className="flex items-start gap-2">
                  <div className={`w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br ${agent.color} p-[2px] flex-shrink-0`}>
                    <div className="w-full h-full rounded-[14px] overflow-hidden bg-[var(--carte)]">
                      {agent.avatar ? (
                        <img src={avatarUrl(agent.avatar)} alt={agent.name} className="w-full h-full object-cover" />
                      ) : (
                        <Bot size={16} className="text-[var(--texte-2)] m-auto" />
                      )}
                    </div>
                  </div>
                  <div className="bg-[var(--carte)] border border-[var(--bord)] rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-2 text-[var(--texte-2)]">
                      <Loader2 size={16} className="animate-spin" />
                      <span className="text-sm">{agent.name} réfléchit…</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Scenarios Panel */}
            <AnimatePresence>
              {showScenarios && (
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: 'auto' }}
                  exit={{ height: 0 }}
                  className="overflow-hidden border-t border-[var(--bord)] bg-[var(--carte)] flex-shrink-0"
                >
                  <div className="p-3 max-h-48 overflow-y-auto">
                    {!selectedCategory ? (
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(categoryConfig).map(([key, config]) => {
                          const Icon = config.icon;
                          return (
                            <button
                              key={key}
                              onClick={() => setSelectedCategory(key)}
                              className="flex items-center gap-2 p-3 bg-[var(--remplissage)] rounded-xl hover:bg-[var(--remplissage)] transition-colors"
                            >
                              <div className={`w-8 h-8 ${config.color} rounded-lg flex items-center justify-center`}>
                                <Icon size={16} className="text-[var(--sur-couleur)]" />
                              </div>
                              <span className="text-sm font-medium text-[var(--texte)]">{config.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div>
                        <button
                          onClick={() => setSelectedCategory(null)}
                          className="text-sm text-[var(--texte-2)] mb-2 flex items-center gap-1"
                        >
                          ← Retour
                        </button>
                        <div className="space-y-1">
                          {scenarios[selectedCategory] && Object.entries(scenarios[selectedCategory]).map(([id, scenario]) => (
                            <button
                              key={id}
                              onClick={() => selectScenario(selectedCategory, id)}
                              className="w-full text-left p-2 hover:bg-[var(--remplissage)] rounded-lg text-sm"
                            >
                              {scenario.title}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Input Area - Fixed at bottom with safe area */}
            <div className="flex-shrink-0 border-t border-[var(--bord)] bg-[var(--carte)] p-3 pb-safe">
              {/* Quick action buttons */}
              <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] sm:flex items-center gap-2 mb-2 pb-1" data-testid="chat-quick-actions">
                <button
                  onClick={() => setShowScenarios(!showScenarios)}
                  data-testid="chat-scenarios-toggle"
                  className={`px-3 py-1.5 text-xs rounded-[14px] flex items-center gap-1 transition-colors flex-shrink-0 ${
                    showScenarios 
                      ? 'bg-[var(--brand)] text-[var(--sur-couleur)]' 
                      : 'bg-[var(--remplissage)] text-[var(--texte-2)] hover:bg-[var(--remplissage)]'
                  }`}
                >
                  <ClipboardList size={12} />
                  Scénarios
                </button>
                {suggestions.slice(0, 2).map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => sendMessage(s)}
                    className="px-3 py-1.5 bg-[var(--remplissage)] text-[var(--texte-2)] text-xs rounded-full hover:bg-[var(--remplissage)] truncate min-w-0 max-w-[150px]"
                    data-testid={`chat-quick-suggestion-${idx}`}
                    title={s}
                  >
                    {s}
                  </button>
                ))}
              </div>
              
              {/* Input field */}
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Posez votre question..."
                  className="flex-1 min-w-0 bg-[var(--remplissage)] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand)] text-[var(--texte)] placeholder:text-[var(--texte-2)]"
                  disabled={isLoading || historyLoading || !!viewSession}
                  data-testid="chat-input"
                />
                <VoiceAskButton disabled={isLoading || historyLoading || !!viewSession} onText={(t) => sendMessage(t)} />
                <button
                  onClick={() => sendMessage()}
                  disabled={!inputValue.trim() || isLoading || historyLoading || !!viewSession}
                  className="w-12 h-12 bg-[var(--brand)] text-[var(--sur-couleur)] rounded-xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  data-testid="send-message-btn"
                >
                  {isLoading ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <Send size={20} />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatBubble;
